import Component from '@glimmer/component';
import formatDateString from '../../../helpers/format-date-string.ts';
import safeFormatDate from '../../../helpers/safe-format-date.ts';
import { LinkTo } from '@ember/routing';
import { on } from '@ember/modifier';
import { PhTrash } from 'ember-phosphor-icons';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import type Store from '@ember-data/store';
import type GenericRelationshipModel from 'tio-data/models/generic-relationship';
import type Owner from '@ember/owner';
import type EmployeeModel from 'tio-data/models/employee';

interface ProfileRoleDelegationRowSignature {
  Args: {
    delegate: Partial<GenericRelationshipModel>;
    cellClass: string;
    employeeRoute?: string;
    usePersonRoute?: boolean;
    onRevoke?: () => void;
  };
  Element: HTMLElement;
}

export default class ProfileRoleDelegationRowComponent extends Component<ProfileRoleDelegationRowSignature> {
  @service declare store: Store;
  @tracked isLoading = true;
  @tracked employee: EmployeeModel | null = null;

  loadEmployeeData = task(async () => {
    const governorId = this.args.delegate.governorId;
    if (!governorId) {
      // TODO: this seems like it should never happen
      this.isLoading = false;
      return;
    }
    this.employee = await this.store.findRecord<EmployeeModel>('employee', governorId, {
      include: 'person',
    });
    this.isLoading = false;
  });

  constructor(owner: Owner, args: ProfileRoleDelegationRowSignature['Args']) {
    super(owner, args);
    this.loadEmployeeData.perform();
  }

  get linkModelId() {
    if (this.args.usePersonRoute) {
      return this.employee?.person?.id;
    }
    return this.args.delegate.governorId;
  }

  get expirationDate() {
    return this.args.delegate.expiresAfter ? this.args.delegate.expiresAfter : 'N/A';
  }

  <template>
    {{#if this.loadEmployeeData.isRunning}}
      <tr>
        <td class={{@cellClass}} colspan="100">
          <div class="h-4 tio-bg-skeleton rounded-full"></div>
        </td>
      </tr>
    {{else}}
      <tr>
        <td class={{@cellClass}}>
          {{#if this.employee}}
            {{#if @employeeRoute}}
              {{#if this.linkModelId}}
                <LinkTo
                  @route={{@employeeRoute}}
                  @model={{this.linkModelId}}
                >{{this.employee.fullName}}</LinkTo>
              {{else}}
                {{this.employee.fullName}}
              {{/if}}
            {{else}}
              {{this.employee.fullName}}
            {{/if}}
          {{else}}
            <span class="tio-fg-disabled">N/A</span>
          {{/if}}
        </td>
        <td class={{@cellClass}}>{{@delegate.relationshipType}}</td>
        <td class={{@cellClass}}>{{safeFormatDate
            @delegate.createdAt
            month="2-digit"
            day="2-digit"
            year="numeric"
          }}</td>
        <td class={{@cellClass}}>{{if
            @delegate.isActive
            (t "common.active")
            (t "common.deactivated")
          }}</td>
        <td class={{@cellClass}}>{{formatDateString this.expirationDate}}</td>
        {{#if @onRevoke}}
          <td class={{@cellClass}}>
            {{#if @delegate.isActive}}
              <button
                type="button"
                title="Revoke delegation"
                aria-label="Revoke delegation"
                class="text-red-600 hover:text-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 rounded"
                {{on "click" @onRevoke}}
              >
                <PhTrash @size={{16}} />
              </button>
            {{/if}}
          </td>
        {{/if}}
      </tr>
    {{/if}}
  </template>
}
